import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  FiFacebook,
  FiInstagram,
  FiPhone,
  FiMail,
  FiMapPin,
} from "react-icons/fi";
import PrivacyPolicyModal from "../legal/PrivacyPolicyModal";
import "./Footer.css";

const HOVER_PREVIEW_DELAY = 450;

const QUICK_LINKS = [
  { label: "All Services", to: "/services" },
  { label: "Vehicle Cleaning", to: "/services?category=vehicle-cleaning" },
  { label: "Household Cleaning", to: "/services?category=household-cleaning" },
  { label: "Laptop Repair", to: "/services?category=laptop-repair" },
  { label: "Printer Repair", to: "/services?category=printer-repair" },
  { label: "Electrician", to: "/services?category=electrician" },
  { label: "Dress Iron", to: "/services?category=dress-iron" },
  { label: "Hair Cutting", to: "/services?category=hair-cutting" },
];

const Footer = () => {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [openedByHover, setOpenedByHover] = useState(false);
  const hoverTimerRef = useRef(null);

  const clearHoverTimer = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  const handlePrivacyClick = (e) => {
    e.preventDefault();
    clearHoverTimer();
    setOpenedByHover(false);
    setPrivacyOpen(true);
  };

  const handlePrivacyMouseEnter = () => {
    if (!window.matchMedia?.("(hover: hover) and (pointer: fine)").matches)
      return;
    clearHoverTimer();
    hoverTimerRef.current = setTimeout(() => {
      setOpenedByHover(true);
      setPrivacyOpen(true);
    }, HOVER_PREVIEW_DELAY);
  };

  const handlePrivacyMouseLeave = () => {
    clearHoverTimer();
    if (openedByHover) {
      setPrivacyOpen(false);
      setOpenedByHover(false);
    }
  };

  const closePrivacyModal = () => {
    setPrivacyOpen(false);
    setOpenedByHover(false);
  };

  return (
    <footer
      className="footer"
      itemScope
      itemType="https://schema.org/LocalBusiness"
    >
      {/* Hidden machine-readable schema data */}
      <meta itemProp="name" content="Expert At Door" />
      <meta itemProp="url" content="https://www.Expert At Door.com" />
      <meta itemProp="telephone" content="+918981002675" />
      <meta itemProp="email" content="Expert At Door@gmail.com" />
      <span
        itemProp="address"
        itemScope
        itemType="https://schema.org/PostalAddress"
        className="hidden"
      >
        <meta itemProp="addressLocality" content="Kolkata" />
        <meta itemProp="addressRegion" content="West Bengal" />
        <meta itemProp="addressCountry" content="IN" />
      </span>
      <span
        itemProp="aggregateRating"
        itemScope
        itemType="https://schema.org/AggregateRating"
        className="hidden"
      >
        <meta itemProp="ratingValue" content="4.7" />
        <meta itemProp="reviewCount" content="50000" />
      </span>

      <div className="container mx-auto px-4 max-w-7xl">
        {/* ── Main row ── */}
        <div className="footer-main">
          {/* Brand column */}
          <div className="footer-brand">
            <Link to="/" className="footer-logo">
              <img
                src="Expert At DoorLogo1.jpeg"
                alt="Expert At Door — Home Services in Kolkata"
                width="180"
                height="60"
                loading="lazy"
                style={{ borderRadius: 8, objectFit: "contain" }}
              />
            </Link>

            <p className="footer-tagline" itemProp="description">
              Bringing certified cleaning experts to your doorstep. Reliable,
              transparent and professional — across Kolkata.
            </p>

            {/* Trust badges */}
            <div className="footer-badges">
              <span className="footer-badge">✅ Verified Experts</span>
              <span className="footer-badge">🔒 Secure Booking</span>
              <span className="footer-badge">⭐ 4.7 Rated</span>
            </div>

            {/* Social icons */}
            <div className="footer-social">
              <a
                href="https://www.facebook.com/share/1ajDdcwrRg/"
                aria-label="Expert At Door on Facebook"
                rel="noopener noreferrer"
                target="_blank"
              >
                <FiFacebook size={18} />
              </a>
              <a
                href="https://www.instagram.com/Expert At Door?igsh=MW9tNm5leXEzdGJqNQ=="
                aria-label="Expert At Door on Instagram"
                rel="noopener noreferrer"
                target="_blank"
              >
                <FiInstagram size={18} />
              </a>
            </div>
          </div>

          {/* Vertical divider */}
          <div className="footer-divider-v" />

          {/* Contact column */}
          <address className="footer-contact-col not-italic">
            <h4 className="footer-col-heading">Get in Touch</h4>

            <a
              href="tel:+918981002675"
              className="footer-contact-item"
              itemProp="telephone"
            >
              <span className="contact-icon">
                <FiPhone size={15} />
              </span>
              +91 89810 02675
            </a>

            <a
              href="mailto:Expert At Door@gmail.com"
              className="footer-contact-item"
              itemProp="email"
            >
              <span className="contact-icon">
                <FiMail size={15} />
              </span>
              Expert At Door@gmail.com
            </a>

            <div className="footer-contact-item" itemProp="address">
              <span className="contact-icon">
                <FiMapPin size={15} />
              </span>
              Kolkata, West Bengal, India
            </div>

            <div className="footer-hours">
              <p className="hours-label">Working Hours</p>
              <p itemProp="openingHours" content="Mo-Sa 10:00-19:00">
                Mon – Sat &nbsp;·&nbsp; 10 AM – 7 PM
              </p>
            </div>
          </address>
        </div>

        {/* ── Bottom bar ── */}
        <div className="footer-bottom">
          <p>
            © {new Date().getFullYear()} Expert At Door. All rights reserved.
          </p>
          <div className="footer-bottom-links">
            <a
              href="/privacy"
              className="footer-privacy-trigger"
              onClick={handlePrivacyClick}
              onMouseEnter={handlePrivacyMouseEnter}
              onMouseLeave={handlePrivacyMouseLeave}
              aria-haspopup="dialog"
              aria-expanded={privacyOpen}
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>

      <PrivacyPolicyModal
        open={privacyOpen}
        onClose={closePrivacyModal}
        variant={openedByHover ? "hover" : "click"}
      />
    </footer>
  );
};

export default Footer;
